import { lazy, Suspense, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useMessagesStore } from 'src/domain/stores/useMessagesStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import AuthNavigator from 'src/presentation/navigation/navigators/AuthNavigator';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import LazyScreenBoundary from 'src/presentation/components/organisms/LazyScreenBoundary';
import type { RootNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import type LoadedMainStackNavigator from 'src/presentation/navigation/navigators/MainStackNavigator';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';
import { useActiveLanguage } from 'src/presentation/hooks/useActiveLanguage';

const RootStack = createStackNavigator<RootNavigation>();

// A separate web chunk, so the login screen does not wait for every signed-in screen.
export const loadMainStackNavigator = () =>
	import('src/presentation/navigation/navigators/MainStackNavigator').then((module) => {
		preloadedMainStackNavigator = module.default;
		return module;
	});

let preloadedMainStackNavigator: typeof LoadedMainStackNavigator | null = null;

function MainStackNavigator() {
	// Fixed at mount: switching from the lazy to the preloaded component later would remount the stack.
	const [Preloaded] = useState(() => preloadedMainStackNavigator);
	// A rejected lazy component stays rejected, so a retry needs a new one.
	const [LazyMainStackNavigator, setLazyMainStackNavigator] = useState(() => lazy(loadMainStackNavigator));

	if (Preloaded) {
		return <Preloaded />;
	}

	return (
		<LazyScreenBoundary onRetry={() => setLazyMainStackNavigator(() => lazy(loadMainStackNavigator))}>
			<Suspense fallback={<LoadingIndicator style={styles.fallback} />}>
				<LazyMainStackNavigator />
			</Suspense>
		</LazyScreenBoundary>
	);
}

export default function RootNavigator() {
	useActiveLanguage();

	const { isAuthenticated } = useAuthStore();
	const { getApiMessages } = useMessagesStore();
	const { captureEvent } = useServicesStore();

	useEffect(() => {
		if (isAuthenticated) {
			getApiMessages().catch((error: ApiRequestError) => {
				captureEvent({ message: 'Fetching the messages after sign in failed.', extra: { error } });
			});
		}
	}, [captureEvent, getApiMessages, isAuthenticated]);

	return (
		<RootStack.Navigator screenOptions={{ headerShown: false }}>
			{!isAuthenticated ? (
				<RootStack.Screen component={AuthNavigator} name={Constants.navigatorAuth} />
			) : (
				<RootStack.Screen component={MainStackNavigator} name={Constants.navigatorMain} />
			)}
		</RootStack.Navigator>
	);
}

const styles = StyleSheet.create({
	fallback: {
		flex: Dimens.flex1,
	},
});
